import { TreeNode } from './../../modules/tank-move/services/move-tank.service';
import { DataManager, Query } from '@syncfusion/ej2-data';
import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  EventEmitter,
  Output,
  ViewChild,
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { isArray, set } from 'lodash';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

type TreeSide = 'left' | 'right';

const TREE_ITEM_SIZE_PX = 32;
const SHORT_TOKEN_LENGTH = 2;

@Component({
  selector: 'app-dual-treeview',
  templateUrl: './dual-treeview.component.html',
  styleUrls: ['./dual-treeview.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DualTreeViewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('leftViewport') leftViewport: CdkVirtualScrollViewport;
  @ViewChild('rightViewport') rightViewport: CdkVirtualScrollViewport;

  @Input() dataMapper: (data: any) => {} = null;
  @Input() batchLoadLocations: (orgIds: string[]) => Promise<{ organizations: any[]; locations: any[]; tanks: any[] }> | null = null;
  @Input() batchLoadTanks: (locationIds: string[]) => Promise<{ tanks: any[] }> | null = null;
  @Input() loadReceivingLocationsOnly: () => Promise<{ organizations: any[]; locations: any[] }> | null = null;
  @Input() searchTextPlaceholder: string | null = null;
  @Input() rightTreeHeader: string | null = null;
  @Input() rightTreeHeaderCount: number | null = null;
  @Input() leftFilterToggleLabel: string | null = null;
  @Input() showOrgChildCounts: boolean = true;

  @Output() rightTreeDataChanged: EventEmitter<TreeNode[]> = new EventEmitter<
    TreeNode[]
  >();

  private _treeViewData: TreeNode[] | DataManager = null;
  protected working = false;

  public get treeViewData(): TreeNode[] | DataManager {
    return this._treeViewData;
  }
  @Input()
  public set treeViewData(value: TreeNode[] | DataManager) {
    this._treeViewData = value;
  }

  @Output() dataLoadStarting = new EventEmitter<void>();
  @Output() dataLoadCompleted = new EventEmitter<void>();

  static readonly MIN_SEARCH_CHARACTERS_REQUIRED = 1;
  readonly DEBUG_MODE = false;
  readonly itemSize = TREE_ITEM_SIZE_PX;
  public leftDataSource: DataManager;
  public leftDataSourceFiltered: TreeNode[] = [];
  public rightDataSource: TreeNode[] = [];
  private isReceivingLocationsChecked = false;
  protected leftFilterTerm = '';
  private leftFilterTokens: string[] = [];
  protected rightFilterTerm = '';
  
  private leftRefreshRequestVersion = 0;

  private _hasNodesLeft = false;
  private _hasNodesRight = false;
  private _hasCheckedNodesLeft = false;
  private _hasCheckedNodesRight = false;

  protected loadingNodeIds = new Set<string>();

  protected isExpandAllState = false;
  private receivingToggleExpandedOrgIds: Set<string> | null = null;

  private preloadDebounceMs = 400;
  private scrollSub: Subscription | null = null;
  private tooltipScrollSub: Subscription | null = null;
  protected tooltipDisabledDuringScroll = false;
  private readonly tooltipScrollEndMs = 180;

  private _cachedLeftFlattened: TreeNode[] = [];
  private _cachedRightFlattened: TreeNode[] = [];
  private _leftFlattenCacheValid = false;
  private _rightFlattenCacheValid = false;
  private _leftRebuildScheduled = false;
  private _rightRebuildScheduled = false;

  constructor(private _cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: import('@angular/core').SimpleChanges): void {
    const treeViewDataChanged = changes['treeViewData']?.firstChange ||
      (changes['treeViewData'] && changes['treeViewData'].previousValue !== changes['treeViewData'].currentValue);
    if (!treeViewDataChanged || !this.dataMapper || !this.treeViewData) return;
    this._treeViewData = isArray(this.treeViewData)
      ? new DataManager(this.treeViewData as unknown as TreeNode[])
      : (this.treeViewData as DataManager);
    this.leftDataSource = this._treeViewData as DataManager;
    this.refreshLeftData();
  }

  ngOnInit(): void {
    this.leftDataSourceFiltered = [];
    this.updateRightTreeData([]);
  }

  ngAfterViewInit(): void {
    if (this.leftViewport?.elementRef?.nativeElement) {
      this.scrollSub = fromEvent(
        this.leftViewport.elementRef.nativeElement,
        'scroll'
      )
        .pipe(debounceTime(this.preloadDebounceMs))
        .subscribe(() => this.preloadVisibleOrgChildren());
    }
    const leftEl = this.leftViewport?.elementRef?.nativeElement;
    const rightEl = this.rightViewport?.elementRef?.nativeElement;
    const sources = [leftEl, rightEl].filter(Boolean).map((el) => fromEvent(el, 'scroll'));
    if (sources.length > 0) {
      this.tooltipScrollSub = merge(...sources)
        .pipe(
          tap(() => {
            this.tooltipDisabledDuringScroll = true;
            this._cdr.markForCheck();
          }),
          debounceTime(this.tooltipScrollEndMs)
        )
        .subscribe(() => {
          this.tooltipDisabledDuringScroll = false;
          this._cdr.markForCheck();
        });
    }
  }

  ngOnDestroy(): void {
    this.scrollSub?.unsubscribe();
    this.tooltipScrollSub?.unsubscribe();
  }

  protected get leftDisplayList(): TreeNode[] {
    let base: TreeNode[];
    if (!this.isReceivingLocationsChecked) {
      base = this.leftDataSourceFiltered;
    } else {
      const nodeById = new Map(this.leftDataSourceFiltered.map((n) => [n.id, n]));
      const includedIds = new Set<string>();
      this.leftDataSourceFiltered.forEach((node) => {
        if (!node.toggle) return;
        let current: TreeNode | undefined = node;
        while (current) {
          includedIds.add(current.id);
          if (!current.parentId) break;
          current = nodeById.get(current.parentId);
        }
      });
      base = this.leftDataSourceFiltered.filter((n) => includedIds.has(n.id));
    }
    return base;
  }

  protected get visibleNodesLeft(): TreeNode[] {
    const shouldPruneLoadedParents = !this.leftFilterTerm?.trim();
    if (this._leftFlattenCacheValid) return this._cachedLeftFlattened;
    if (this._cachedLeftFlattened.length === 0) {
      this._cachedLeftFlattened = this.buildFlattenedVisible(
        this.leftDisplayList,
        shouldPruneLoadedParents
      );
      this._leftFlattenCacheValid = true;
      return this._cachedLeftFlattened;
    }
    if (!this._leftRebuildScheduled) {
      this._leftRebuildScheduled = true;
      setTimeout(() => this.rebuildLeftFlattenCache(), 0);
    }
    return this._cachedLeftFlattened;
  }

  protected get visibleNodesRight(): TreeNode[] {
    if (this._rightFlattenCacheValid) return this._cachedRightFlattened;
    if (this._cachedRightFlattened.length === 0) {
      this._cachedRightFlattened = this.buildFlattenedVisible(this.rightDataSource);
      this._rightFlattenCacheValid = true;
      return this._cachedRightFlattened;
    }
    if (!this._rightRebuildScheduled) {
      this._rightRebuildScheduled = true;
      setTimeout(() => this.rebuildRightFlattenCache(), 0);
    }
    return this._cachedRightFlattened;
  }

  private rebuildLeftFlattenCache(): void {
    this._leftRebuildScheduled = false;
    const shouldPruneLoadedParents = !this.leftFilterTerm?.trim();
    this._cachedLeftFlattened = this.buildFlattenedVisible(
      this.leftDisplayList,
      shouldPruneLoadedParents
    );
    this._leftFlattenCacheValid = true;
    this._cdr.markForCheck();
  }

  private rebuildRightFlattenCache(): void {
    this._rightRebuildScheduled = false;
    this._cachedRightFlattened = this.buildFlattenedVisible(this.rightDataSource);
    this._rightFlattenCacheValid = true;
    this._cdr.markForCheck();
  }

  private nodesWithL3Descendants(nodes: TreeNode[]): Set<string> {
    const idSet = new Set(nodes.map((n) => n.id));
    const childrenByParent = new Map<string, TreeNode[]>();
    nodes.forEach((n) => {
      const pid = n.parentId ?? '';
      if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
      childrenByParent.get(pid)!.push(n);
    });
    const hasL3 = new Set<string>();
    const visit = (nodeId: string): boolean => {
      if (hasL3.has(nodeId)) return true;
      const node = nodes.find((n) => n.id === nodeId);
      if (node?.level === 3) {
        hasL3.add(nodeId);
        return true;
      }
      const children = childrenByParent.get(nodeId) ?? [];
      const descendantHasL3 = children.some((c) => visit(c.id));
      if (descendantHasL3) hasL3.add(nodeId);
      return descendantHasL3;
    };
    nodes.forEach((n) => visit(n.id));
    return hasL3;
  }

  private buildFlattenedVisible(nodes: TreeNode[], pruneLoadedParents = true): TreeNode[] {
    if (!nodes.length) return [];
    const hasAnyL3 = pruneLoadedParents && nodes.some((n) => (n.level ?? 0) === 3);
    const l3Ids = hasAnyL3 ? this.nodesWithL3Descendants(nodes) : new Set<string>();
    const filtered = hasAnyL3
      ? nodes.filter((n) => {
          const level = n.level ?? 0;
          if (level === 3) return true;
          if (level !== 1 && level !== 2) return true;
          if (!(n.areChildrenLoaded == true)) return true;
          if (level === 1) {
            const childLocs = nodes.filter((c) => c.parentId === n.id && c.level === 2);
            const allLocsFullyLoaded = childLocs.length > 0 && childLocs.every((c) => c.areChildrenLoaded === true);
            if (!allLocsFullyLoaded) return true;
          }
          return l3Ids.has(n.id);
        })
      : nodes;
    const idSet = new Set(filtered.map((n) => n.id));
    const childrenByParent = new Map<string, TreeNode[]>();
    filtered.forEach((n) => {
      const pid = n.parentId ?? '';
      if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
      childrenByParent.get(pid)!.push(n);
    });
    childrenByParent.forEach((arr) =>
      arr.sort(
        (a, b) =>
          (a.level ?? 0) - (b.level ?? 0) || a.name.localeCompare(b.name)
      )
    );
    const rootCandidates = filtered.filter(
      (n) => !n.parentId || !idSet.has(n.parentId)
    );
    const roots = rootCandidates.sort(
      (a, b) =>
        (a.level ?? 0) - (b.level ?? 0) || a.name.localeCompare(b.name)
    );
    const out: TreeNode[] = [];
    const add = (node: TreeNode) => {
      out.push(node);
      if (!node.expanded) return;
      const children = childrenByParent.get(node.id) ?? [];
      children.forEach(add);
    };
    roots.forEach(add);
    return out;
  }

  private invalidateLeftFlattenCache(): void {
    this._leftFlattenCacheValid = false;
  }

  private clearLeftFlattenCache(): void {
    this._cachedLeftFlattened = [];
    this._leftFlattenCacheValid = false;
    this._leftRebuildScheduled = false;
  }

  private invalidateRightFlattenCache(): void {
    this._rightFlattenCacheValid = false;
  }

  private updateChildCounts(nodes: TreeNode[]): void {
    const parentCountMap = new Map<string, number>();
    nodes.forEach((c) => {
      if (c.parentId != null) {
        parentCountMap.set(
          c.parentId,
          (parentCountMap.get(c.parentId) ?? 0) + 1
        );
      }
    });
    nodes.forEach((n) => {
      if (!n.hasChildren) return;
      set(n, 'childCount', n.areChildrenLoaded ? (parentCountMap.get(n.id) ?? 0) : (n.childCount ?? 0));
    });
  }

  protected isNodeLoading(nodeId: string): boolean {
    return this.loadingNodeIds.has(nodeId);
  }

  protected onExpandKeydown(event: KeyboardEvent, node: TreeNode, side: TreeSide): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onExpandClick(node, side);
    }
  }

  protected onExpandClick(node: TreeNode, side: TreeSide): void {
    if (side === 'right') {
      if (node.hasChildren && !node.areChildrenLoaded) {
        node.expanded = true;
        this.invalidateRightFlattenCache();
        this.loadingNodeIds.add(node.id);
        this._cdr.markForCheck();
        this.dataLoadStarting.emit();
        this.loadChildrenForRightNode(node)
          .then(() => {
            this.loadingNodeIds.delete(node.id);
            this.dataLoadCompleted.emit();
            this._cdr.markForCheck();
          })
          .catch(() => {
            this.loadingNodeIds.delete(node.id);
            this.dataLoadCompleted.emit();
            this._cdr.markForCheck();
          });
        return;
      }
      node.expanded = !node.expanded;
      this.invalidateRightFlattenCache();
      this._cdr.markForCheck();
      return;
    }
    if (node.expanded) {
      node.expanded = false;
      this.rebuildLeftAfterExpandCollapse();
      return;
    }
    if (!node.hasChildren || node.areChildrenLoaded) {
      node.expanded = !node.expanded;
      this.rebuildLeftAfterExpandCollapse();
      return;
    }
    node.expanded = true;
    this.rebuildLeftAfterExpandCollapse();
    this.loadingNodeIds.add(node.id);
    this._cdr.markForCheck();
    this.dataLoadStarting.emit();
    const query = new Query().where('parentId', 'equal', node.id);
    this.leftDataSource.executeQuery(query).then(
      (data: any) => {
        this.processLazyLoad(data, node);
        this.loadingNodeIds.delete(node.id);
        this.rebuildLeftAfterExpandCollapse();
        this._cdr.markForCheck();
        this.dataLoadCompleted.emit();
      },
      () => {
        this.loadingNodeIds.delete(node.id);
        this.rebuildLeftAfterExpandCollapse();
        this._cdr.markForCheck();
        this.dataLoadCompleted.emit();
      }
    );
  }

  private rebuildLeftAfterExpandCollapse(): void {
    this.clearLeftFlattenCache();
    this.rebuildLeftFlattenCache();
  }

  private processLazyLoad(data: any, parentNode: TreeNode): void {
    const results = (this.dataMapper(data) as TreeNode[]).map((node) =>
      this.normalizeNodeLoadState({
        ...node,
      })
    );
    if (parentNode.isChecked) {
      const isChecked = parentNode.isChecked as string;
      results.forEach((n) => (n.isChecked = isChecked));
    }
    if (parentNode.toggle) {
      results.forEach((n) => (n.toggle = parentNode.toggle));
    }
    parentNode.areChildrenLoaded = true;
    this.applyLeftNodeMatchState(results);
    const existingIds = new Set(this.leftDataSourceFiltered.map((n) => n.id));
    results.forEach((n) => {
      if (!existingIds.has(n.id)) {
        this.leftDataSourceFiltered.push(n);
        existingIds.add(n.id);
      }
    });
    this.invalidateLeftFlattenCache();
    this.updateChildCounts(this.leftDataSourceFiltered);
  }

  private processLazyLoadIntoRight(data: any, parentNode: TreeNode): void {
    const results = (this.dataMapper(data) as TreeNode[]).map((n) =>
      this.normalizeNodeLoadState({ ...n })
    );
    if (parentNode.isChecked) {
      const isChecked = parentNode.isChecked as string;
      results.forEach((n) => (n.isChecked = isChecked));
    }
    if (parentNode.toggle) {
      results.forEach((n) => (n.toggle = parentNode.toggle));
    }
    parentNode.areChildrenLoaded = true;
    const existingIds = new Set(this.rightDataSource.map((n) => n.id));
    results.forEach((n) => {
      if (!existingIds.has(n.id)) {
        this.rightDataSource.push(n);
        existingIds.add(n.id);
      }
    });
    this.updateRightTreeData(this.rightDataSource);
  }

  private loadChildrenForRightNode(node: TreeNode): Promise<void> {
    const level = node.level ?? 0;
    if (level === 1) {
      if (typeof this.batchLoadLocations === 'function') {
        return this.batchLoadLocations([node.id]).then((response) => {
          const locations = response?.locations ?? [];
          const numericId = parseInt(node.id.replace(/^o/, ''), 10);
          const locationsForOrg = locations.filter(
            (loc: any) => (loc.ParentId ?? loc.parentId) === numericId
          );
          const slice = {
            result: { organizations: [], locations: locationsForOrg, tanks: [] },
          };
          this.processLazyLoadIntoRight(slice, node);
        });
      }
      return this.leftDataSource
        .executeQuery(new Query().where('parentId', 'equal', node.id))
        .then((data: any) => this.processLazyLoadIntoRight(data, node));
    }
    if (level === 2) {
      if (typeof this.batchLoadTanks === 'function') {
        return this.batchLoadTanks([node.id]).then((response) => {
          const tanks = response?.tanks ?? [];
          const slice = {
            result: { organizations: [], locations: [], tanks },
          };
          this.processLazyLoadIntoRight(slice, node);
        });
      }
      return this.leftDataSource
        .executeQuery(new Query().where('parentId', 'equal', node.id))
        .then((data: any) => this.processLazyLoadIntoRight(data, node));
    }
    return Promise.resolve();
  }

  protected onCheckboxChange(node: TreeNode, side: TreeSide, checked: boolean): void {
    node.isChecked = checked;
    if (side === 'left') {
      if (checked) {
        node.expanded = true;
        this.invalidateLeftFlattenCache();
        const level = node.level ?? 0;
        if (node.hasChildren && level === 1) {
          this.loadNodeChildrenThenSetDescendantsChecked(node);
        } else if (node.hasChildren && !node.areChildrenLoaded) {
          this.loadNodeChildrenThenSetDescendantsChecked(node);
        } else {
          this.setDescendantsChecked(node.id, checked, this.leftDataSourceFiltered);
        }
      } else {
        this.setDescendantsChecked(node.id, checked, this.leftDataSourceFiltered);
      }
      this._hasCheckedNodesLeft = this.leftDisplayList.some((n) => n.isChecked);
    } else {
      if (checked) {
        node.expanded = true;
        this.invalidateRightFlattenCache();
      }
      this.setDescendantsChecked(node.id, checked, this.rightDataSource);
      this._hasCheckedNodesRight = this.rightDataSource.some((n) => n.isChecked);
    }
    this._cdr.markForCheck();
  }

  private setDescendantsChecked(rootId: string, checked: boolean, list: TreeNode[]): void {
    const children = list.filter((n) => n.parentId === rootId);
    children.forEach((c) => {
      c.isChecked = checked;
      this.setDescendantsChecked(c.id, checked, list);
    });
  }

  private loadTanksForLoadedLocations(org: TreeNode): void {
    const locs = this.leftDataSourceFiltered.filter((n) => n.parentId === org.id && n.level === 2);
    if (locs.length === 0) return;
    locs.forEach((l) => (l.expanded = true));
    this.invalidateLeftFlattenCache();
    const toLoad = locs.filter((l) => l.hasChildren && !l.areChildrenLoaded);
    if (toLoad.length > 0) {
      this.loadChildrenForLocations(toLoad, false);
    }
  }

  private loadNodeChildrenThenSetDescendantsChecked(node: TreeNode): void {
    const level = node.level ?? 0;
    const done = () => {
      this.setDescendantsChecked(node.id, true, this.leftDataSourceFiltered);
      this._hasCheckedNodesLeft = this.leftDisplayList.some((n) => n.isChecked);
      this._cdr.markForCheck();
    };
    if (level === 1) {
      this.loadChildrenForOrgs([node], false).then(() => {
        const locs = this.leftDataSourceFiltered.filter((n) => n.parentId === node.id);
        locs.forEach((l) => (l.expanded = true));
        this.invalidateLeftFlattenCache();
        const toLoad = locs.filter((l) => l.hasChildren && !l.areChildrenLoaded);
        if (toLoad.length > 0) {
          return this.loadChildrenForLocations(toLoad, false, { loadAllForOrgSelection: true }).then(done);
        }
        done();
      });
    } else if (level === 2) {
      this.loadChildrenForLocations([node], false).then(done);
    } else {
      done();
    }
  }

  protected isNodeChecked(node: TreeNode): boolean {
    return !!node.isChecked;
  }

  protected hasCheckedNodes = (side: TreeSide): boolean =>
    side === 'left' ? this._hasCheckedNodesLeft : this._hasCheckedNodesRight;

  protected hasNodes = (side: TreeSide): boolean =>
    side === 'left' ? this.leftDisplayList.length > 0 : this._hasNodesRight;

  protected trackByNodeId = (_index: number, node: TreeNode): string => node.id;

  protected getNodeIcon(node: TreeNode): string {
    const level = node.level ?? 0;
    if (level === 1) return 'business';
    if (level === 2) return 'place';
    return node.icon === 'skytank-gateway' ? 'router' : 'storage';
  }

  protected shouldShowChildCount(node: TreeNode): boolean {
    if (!node?.hasChildren) return false;
    if (this.showOrgChildCounts) return true;
    return (node.level ?? 0) !== 1;
  }

  protected onFilterToggleChange($event: MatSlideToggleChange): void {
    this.isReceivingLocationsChecked = $event.checked;
    if (this.isReceivingLocationsChecked) {
      const hasReceivingNodes = this.leftDataSourceFiltered.some((n) => n.toggle === true);
      if (!hasReceivingNodes && typeof this.loadReceivingLocationsOnly === 'function') {
        this.dataLoadStarting.emit();
        this.loadReceivingLocationsOnly()
          .then((res) => {
            const orgs = (res?.organizations ?? []).map((o: any) => ({
              id: 'o' + (o.Id ?? o.id),
              name: o.Name ?? o.name,
              level: 1,
              hasChildren: true,
              areChildrenLoaded: true,
              expanded: true,
              childCount: o.ChildCount ?? o.childCount ?? 0,
            } as TreeNode));
            const locs = (res?.locations ?? []).map((l: any) => ({
              id: 'l' + (l.Id ?? l.id),
              name: l.Name ?? l.name,
              parentId: 'o' + (l.ParentId ?? l.parentId),
              level: 2,
              hasChildren: true,
              toggle: true,
              areChildrenLoaded: false,
              childCount: l.ChildCount ?? l.childCount ?? 0,
            } as TreeNode));
            const existingIds = new Set(this.leftDataSourceFiltered.map((n) => n.id));
            orgs.forEach((node) => {
              if (!existingIds.has(node.id)) {
                this.leftDataSourceFiltered.push(node);
                existingIds.add(node.id);
              }
            });
            this.leftDataSourceFiltered.forEach((n) => {
              if (n.level === 1 && orgs.some((o) => o.id === n.id)) {
                n.areChildrenLoaded = true;
                n.expanded = true;
              }
            });
            locs.forEach((node) => {
              if (!existingIds.has(node.id)) {
                this.leftDataSourceFiltered.push(node);
                existingIds.add(node.id);
              }
            });
            this.updateChildCounts(this.leftDataSourceFiltered);
            this.leftDataSourceFiltered.forEach((n) => {
              if (n.level === 1 && orgs.some((o) => o.id === n.id)) {
                n.areChildrenLoaded = true;
                n.expanded = true;
              }
            });
            this.applyReceivingFilterState();
            this.clearLeftFlattenCache();
            this.rebuildLeftFlattenCache();
            this.dataLoadCompleted.emit();
            this._cdr.markForCheck();
          })
          .catch(() => {
            this.dataLoadCompleted.emit();
            this._cdr.markForCheck();
          });
        return;
      }
      this.applyReceivingFilterState();
    } else if (this.receivingToggleExpandedOrgIds) {
      this.leftDataSourceFiltered.forEach((n) => {
        if (n.level === 1 || n.level === 2) {
          n.expanded = this.receivingToggleExpandedOrgIds!.has(n.id);
        }
      });
      this.receivingToggleExpandedOrgIds = null;
    }
    this._hasNodesLeft = this.leftDisplayList.length > 0;
    this._hasCheckedNodesLeft = this.leftDisplayList.some((n) => n.isChecked);
    this.invalidateLeftFlattenCache();
    this._cdr.markForCheck();
  }

  private applyReceivingFilterState(): void {
    this.receivingToggleExpandedOrgIds = new Set(
      this.leftDataSourceFiltered
        .filter((n) => (n.level === 1 || n.level === 2) && n.expanded)
        .map((n) => n.id)
    );
    const nodeById = new Map(this.leftDataSourceFiltered.map((n) => [n.id, n]));
    const orgIds = new Set<string>();
    this.leftDataSourceFiltered.forEach((node) => {
      if (!node.toggle) return;
      let current: TreeNode | undefined = node;
      while (current) {
        if (current.level === 1) {
          orgIds.add(current.id);
          break;
        }
        if (!current.parentId) break;
        current = nodeById.get(current.parentId);
      }
    });
    this.leftDataSourceFiltered.forEach((n) => {
      if (n.level === 1 && orgIds.has(n.id)) n.expanded = true;
    });
    this._hasNodesLeft = this.leftDisplayList.length > 0;
    this._hasCheckedNodesLeft = this.leftDisplayList.some((n) => n.isChecked);
    this.invalidateLeftFlattenCache();
  }

  protected onExpandCollapseAllToggle(): void {
    this.isExpandAllState = !this.isExpandAllState;
   if (!this.isExpandAllState) {
      this.leftDataSourceFiltered.forEach((n) => {
        if (n.level === 1 || n.level === 2) n.expanded = false;
      });
      this.invalidateLeftFlattenCache();
      this._cdr.markForCheck();
      return;
    }
    const orgs = this.leftDataSourceFiltered.filter((n) => n.level === 1);
    orgs.forEach((n) => (n.expanded = true));
    this.invalidateLeftFlattenCache();
    this._cdr.markForCheck();
    setTimeout(() => {
      const batches = this.getPrioritizedOrgBatches();
      this.loadChildrenForOrgs(batches.viewport, true)
        .then(() => {
          this.rebuildLeftFlattenCache();
          return Promise.resolve();
        })
        .then(() => {
          if (batches.near.length > 0) {
            return this.loadChildrenForOrgs(batches.near, false);
          }
          return Promise.resolve();
        })
        .then(() => {
          this.rebuildLeftFlattenCache();
          return Promise.resolve();
        })
        .then(() => this._cdr.markForCheck());
    }, 0);
  }

  private preloadVisibleOrgChildren(): void {
    if (!this.leftDataSource || this.leftFilterTerm?.trim()) return;
    const orgsToLoad = this.getVisibleOrgsThatNeedLoad();
    if (orgsToLoad.length > 0) {
      this.loadChildrenForOrgs(orgsToLoad, false);
    }
    this.preloadVisibleLocationChildren();
  }

  private preloadVisibleLocationChildren(): void {
    if (!this.leftDataSource) return;
    const locsToLoad = this.getVisibleLocationsThatNeedLoad();
    if (locsToLoad.length === 0) return;
    this.loadChildrenForLocations(locsToLoad, false);
  }

  private static readonly MAX_ORGS_TO_LOAD_PER_BATCH = 150;
  private static readonly NEAR_VIEWPORT_BUFFER = 150;
  private static readonly MAX_VIEWPORT_ORGS = 40;
  private static readonly MAX_LOCATIONS_TO_LOAD_PER_BATCH = 100;

  private getPrioritizedOrgBatches(): {
    viewport: TreeNode[];
    near: TreeNode[];
    rest: TreeNode[];
  } {
    const orgsNeedingLoad = this.leftDataSourceFiltered.filter(
      (n) => n.level === 1 && n.hasChildren && !n.areChildrenLoaded
    );
    if (orgsNeedingLoad.length === 0)
      return { viewport: [], near: [], rest: [] };
    if (!this.leftViewport)
      return { viewport: [], near: [], rest: orgsNeedingLoad };
    const visibleNodes = this.visibleNodesLeft;
    const range = this.leftViewport.getRenderedRange();
    const orgIdToIndex = new Map<string, number>();
    visibleNodes.forEach((n, i) => {
      if (n.level === 1) orgIdToIndex.set(n.id, i);
    });
    const byIndex = orgsNeedingLoad
      .filter((org) => orgIdToIndex.has(org.id))
      .sort(
        (a, b) =>
          (orgIdToIndex.get(a.id) ?? 0) - (orgIdToIndex.get(b.id) ?? 0)
      );
    const viewportEndIndex = Math.min(
      range.end,
      visibleNodes.length,
      range.start + DualTreeViewComponent.MAX_VIEWPORT_ORGS
    );
    const viewport: TreeNode[] = [];
    const near: TreeNode[] = [];
    for (const org of byIndex) {
      const i = orgIdToIndex.get(org.id)!;
      if (i < range.start) continue;
      if (i < viewportEndIndex) {
        viewport.push(org);
      } else if (near.length < DualTreeViewComponent.NEAR_VIEWPORT_BUFFER) {
        near.push(org);
      }
    }
    const viewportIds = new Set(viewport.map((o) => o.id));
    const nearIds = new Set(near.map((o) => o.id));
    const rest = byIndex.filter(
      (o) => !viewportIds.has(o.id) && !nearIds.has(o.id)
    );
    const max = DualTreeViewComponent.MAX_ORGS_TO_LOAD_PER_BATCH;
    return {
      viewport: viewport.slice(0, max),
      near: near.slice(0, max),
      rest,
    };
  }

  private getVisibleOrgsThatNeedLoad(): TreeNode[] {
    const orgs = this.leftDataSourceFiltered.filter(
      (n) =>
        n.level === 1 &&
        n.hasChildren &&
        !n.areChildrenLoaded &&
        n.expanded
    );
    if (!this.leftViewport || orgs.length === 0) return [];
    const visibleNodes = this.visibleNodesLeft;
    const range = this.leftViewport.getRenderedRange();
    const visibleOrgIds = new Set<string>();
    const buffer = 100;
    const start = Math.max(0, range.start - buffer);
    const end = Math.min(visibleNodes.length, range.end + buffer);
    for (let i = start; i < end; i++) {
      const node = visibleNodes[i];
      if (node.level === 1) visibleOrgIds.add(node.id);
    }
    return orgs
      .filter((n) => visibleOrgIds.has(n.id))
      .slice(0, DualTreeViewComponent.MAX_ORGS_TO_LOAD_PER_BATCH);
  }

  private getVisibleLocationsThatNeedLoad(): TreeNode[] {
    const locs = this.leftDataSourceFiltered.filter(
      (n) => n.level === 2 && n.hasChildren && !n.areChildrenLoaded && n.expanded
    );
    if (!this.leftViewport || locs.length === 0) return [];
    const visibleNodes = this.visibleNodesLeft;
    const range = this.leftViewport.getRenderedRange();
    const visibleLocationIds = new Set<string>();
    const buffer = 100;
    const start = Math.max(0, range.start - buffer);
    const end = Math.min(visibleNodes.length, range.end + buffer);
    for (let i = start; i < end; i++) {
      const node = visibleNodes[i];
      if (node.level === 2) visibleLocationIds.add(node.id);
    }
    return locs
      .filter((n) => visibleLocationIds.has(n.id))
      .slice(0, DualTreeViewComponent.MAX_LOCATIONS_TO_LOAD_PER_BATCH);
  }

  private static readonly LOAD_ORGS_CONCURRENCY = 4;
  private static readonly LOAD_LOCATIONS_CONCURRENCY = 6;

  private loadChildrenForOrgs(orgsToLoad: TreeNode[], showOverlay: boolean): Promise<void> {
    orgsToLoad = orgsToLoad.slice(0, DualTreeViewComponent.MAX_ORGS_TO_LOAD_PER_BATCH);
    const toLoad = orgsToLoad.filter((o) => !this.loadingNodeIds.has(o.id));
    if (toLoad.length === 0) return Promise.resolve();
    toLoad.forEach((o) => this.loadingNodeIds.add(o.id));
    if (showOverlay) {
      this.dataLoadStarting.emit();
      this.setCursorToWait();
    }
    this._cdr.markForCheck();

    const useBatch =
      typeof this.batchLoadLocations === 'function' && toLoad.length > 0;
    if (useBatch) {
      const orgIdsToRequest = toLoad.map((o) => o.id);
      return this.batchLoadLocations(orgIdsToRequest)
        .then((response) => {
          const locations = response?.locations ?? [];
          const locationsByParentId = new Map<number, any[]>();
          locations.forEach((loc: any) => {
            const pid = loc.ParentId ?? loc.parentId;
            if (!locationsByParentId.has(pid)) locationsByParentId.set(pid, []);
            locationsByParentId.get(pid)!.push(loc);
          });
          toLoad.forEach((org) => {
            const numericId = parseInt(org.id.replace(/^o/, ''), 10);
            const locationsForOrg = locationsByParentId.get(numericId) ?? [];
            const slice = {
              result: { organizations: [], locations: locationsForOrg, tanks: [] },
            };
            this.processLazyLoad(slice, org);
          });
          toLoad.forEach((o) => this.loadingNodeIds.delete(o.id));
          if (showOverlay) {
            this.removeCursorWait();
            this.dataLoadCompleted.emit();
          }
          this._cdr.markForCheck();
        })
        .catch(() => {
          toLoad.forEach((o) => this.loadingNodeIds.delete(o.id));
          if (showOverlay) {
            this.removeCursorWait();
            this.dataLoadCompleted.emit();
          }
          this._cdr.markForCheck();
        })
        .then(() => undefined);
    }

    const runChunk = (chunk: TreeNode[]): Promise<void> => {
      const promises = chunk.map((org) =>
        this.leftDataSource
          .executeQuery(new Query().where('parentId', 'equal', org.id))
          .then((data: any) => ({ status: 'fulfilled' as const, value: { org, data } }))
          .catch(() => ({ status: 'rejected' as const }))
      );
      return Promise.all(promises).then((results) => {
        results.forEach((r) => {
          if (r.status === 'fulfilled' && r.value) {
            this.processLazyLoad(r.value.data, r.value.org);
          }
        });
      });
    };
    const chunks: TreeNode[][] = [];
    for (let i = 0; i < toLoad.length; i += DualTreeViewComponent.LOAD_ORGS_CONCURRENCY) {
      chunks.push(toLoad.slice(i, i + DualTreeViewComponent.LOAD_ORGS_CONCURRENCY));
    }
    let chain = Promise.resolve();
    chunks.forEach((chunk) => {
      chain = chain.then(() => runChunk(chunk));
    });
    return chain.then(() => {
      toLoad.forEach((o) => this.loadingNodeIds.delete(o.id));
      if (showOverlay) {
        this.removeCursorWait();
        this.dataLoadCompleted.emit();
      }
      this._cdr.markForCheck();
    });
  }

  private loadChildrenForLocations(locsToLoad: TreeNode[], showOverlay: boolean, options?: { loadAllForOrgSelection?: boolean }): Promise<void> {
    if (!options?.loadAllForOrgSelection) {
      locsToLoad = locsToLoad.slice(0, DualTreeViewComponent.MAX_LOCATIONS_TO_LOAD_PER_BATCH);
    }
    const toLoad = locsToLoad.filter((l) => !this.loadingNodeIds.has(l.id));
    if (toLoad.length === 0) return Promise.resolve();
    if (this.DEBUG_MODE) {
      console.debug('[DualTreeView] loadChildrenForLocations', { count: toLoad.length, showOverlay });
    }
    const t0 = this.DEBUG_MODE ? performance.now() : 0;
    toLoad.forEach((l) => this.loadingNodeIds.add(l.id));
    if (showOverlay) {
      this.dataLoadStarting.emit();
      this.setCursorToWait();
    }
    this._cdr.markForCheck();

    const useBatch =
      typeof this.batchLoadTanks === 'function' && toLoad.length > 0;
    if (useBatch) {
      const locationIds = toLoad.map((l) => l.id);
      return this.batchLoadTanks(locationIds)
        .then((response) => {
          const tanks = response?.tanks ?? [];
          const tanksByLocationId = new Map<number, any[]>();
          tanks.forEach((t: any) => {
            const locId = t.LocationId ?? t.parentId ?? t.ParentId;
            if (locId == null) return;
            const pid = typeof locId === 'string' ? parseInt(locId.replace(/^l/, ''), 10) : locId;
            if (!tanksByLocationId.has(pid)) tanksByLocationId.set(pid, []);
            tanksByLocationId.get(pid)!.push(t);
          });
          toLoad.forEach((loc) => {
            const numericId = parseInt(loc.id.replace(/^l/, ''), 10);
            const tanksForLoc = tanksByLocationId.get(numericId) ?? [];
            const slice = {
              result: { organizations: [], locations: [], tanks: tanksForLoc },
            };
            this.processLazyLoad(slice, loc);
          });
          toLoad.forEach((l) => this.loadingNodeIds.delete(l.id));
          if (showOverlay) {
            this.removeCursorWait();
            this.dataLoadCompleted.emit();
          }
          if (this.DEBUG_MODE && t0) {
            console.debug('[DualTreeView] loadChildrenForLocations batch done', { ms: Math.round(performance.now() - t0) });
          }
          this._cdr.markForCheck();
        })
        .catch(() => {
          toLoad.forEach((l) => this.loadingNodeIds.delete(l.id));
          if (showOverlay) {
            this.removeCursorWait();
            this.dataLoadCompleted.emit();
          }
          this._cdr.markForCheck();
        })
        .then(() => undefined);
    }

    const runChunk = (chunk: TreeNode[]): Promise<void> => {
      const promises = chunk.map((loc) =>
        this.leftDataSource
          .executeQuery(new Query().where('parentId', 'equal', loc.id))
          .then((data: any) => ({ status: 'fulfilled' as const, value: { loc, data } }))
          .catch(() => ({ status: 'rejected' as const }))
      );
      return Promise.all(promises).then((results) => {
        results.forEach((r) => {
          if (r.status === 'fulfilled' && r.value) {
            this.processLazyLoad(r.value.data, r.value.loc);
          }
        });
      });
    };
    const chunks: TreeNode[][] = [];
    for (let i = 0; i < toLoad.length; i += DualTreeViewComponent.LOAD_LOCATIONS_CONCURRENCY) {
      chunks.push(toLoad.slice(i, i + DualTreeViewComponent.LOAD_LOCATIONS_CONCURRENCY));
    }
    let chain = Promise.resolve();
    chunks.forEach((chunk) => {
      chain = chain.then(() => runChunk(chunk));
    });
    return chain.then(() => {
      toLoad.forEach((l) => this.loadingNodeIds.delete(l.id));
      if (showOverlay) {
        this.removeCursorWait();
        this.dataLoadCompleted.emit();
      }
      if (this.DEBUG_MODE && t0) {
        console.debug('[DualTreeView] loadChildrenForLocations executeQuery done', { ms: Math.round(performance.now() - t0) });
      }
      this._cdr.markForCheck();
    });
  }

  protected onMoveNodes = (fromSide: TreeSide, toSide: TreeSide): void => {
    this.moveLocalNodes(fromSide, toSide);
  };

  protected onMoveAllNodes = (fromSide: TreeSide, toSide: TreeSide): void => {
    this.moveAllLocalNodes(fromSide, toSide);
  };

  private getCheckedIds(side: TreeSide): string[] {
    const list = side === 'left' ? this.leftDisplayList : this.rightDataSource;
    return list.filter((n) => n.isChecked).map((n) => n.id);
  }

  private moveAllLocalNodes(fromSide: TreeSide, toSide: TreeSide): void {
    this.setCursorToWait();
    const expandedIds = this.captureExpandedIds();
    if (fromSide === 'left' && toSide === 'right') {
      const rightIds = new Set(this.rightDataSource.map((n) => n.id));
      const nodesToAdd = this.leftDisplayList.filter(
        (n) => !rightIds.has(n.id)
      );
      nodesToAdd.forEach((n) => {
        this.rightDataSource.push(n);
        rightIds.add(n.id);
      });
      const nodesToRemoveIds = new Set(nodesToAdd.map((n) => n.id));
      this.leftDataSourceFiltered = this.leftDataSourceFiltered.filter(
        (n) => !nodesToRemoveIds.has(n.id)
      );
      this.updateRightTreeData(this.rightDataSource);
      this.updateLeftTreeData(this.leftDataSourceFiltered);
    } else if (fromSide === 'right' && toSide === 'left') {
      const leftIds = new Set(this.leftDataSourceFiltered.map((n) => n.id));
      const nodesToAdd = this.rightDataSource.filter((n) => !leftIds.has(n.id));
      this.leftDataSourceFiltered.push(...nodesToAdd);
      this.rightDataSource = [];
      this.leftDataSourceFiltered.sort((a, b) => {
        const levelA = a.level ?? 0;
        const levelB = b.level ?? 0;
        if (levelA !== levelB) return levelA - levelB;
        return a.name.localeCompare(b.name);
      });
      this.updateRightTreeData(this.rightDataSource);
      this.updateLeftTreeData(this.leftDataSourceFiltered);
    }
    this.restoreExpandedIds(expandedIds);
    this.removeCursorWait();
  }

  private captureExpandedIds(): Set<string> {
    const ids = new Set<string>();
    [...this.leftDataSourceFiltered, ...this.rightDataSource].forEach((n) => {
      if (n.expanded) ids.add(n.id);
    });
    return ids;
  }

  private restoreExpandedIds(ids: Set<string>): void {
    [...this.leftDataSourceFiltered, ...this.rightDataSource].forEach((n) => {
      n.expanded = ids.has(n.id);
    });
    this.invalidateLeftFlattenCache();
    this.invalidateRightFlattenCache();
    this._cdr.markForCheck();
  }

  private removeCursorWait(): void {
    this.working = false;
    this._cdr.markForCheck();
  }

  private setCursorToWait(): void {
    this.working = true;
    this._cdr.markForCheck();
  }

  private refreshLeftData(
    query: Query = new Query(),
    nodeOptions?: Record<string, unknown>
  ): void {
    this.dataLoadStarting.emit();
    const isLazyLoad = query.queries?.some(
      (q: any) => q?.e?.field?.toLocaleLowerCase() === 'parentid'
    );
    const requestVersion = !isLazyLoad
      ? ++this.leftRefreshRequestVersion
      : this.leftRefreshRequestVersion;
    if (!isLazyLoad) {
      this.leftDataSourceFiltered = [];
      this._hasNodesLeft = false;
      this._hasCheckedNodesLeft = false;
      this.clearLeftFlattenCache();
      this._cdr.markForCheck();
    }
    this.leftDataSource.executeQuery(query).then(
      (data: any) => {
        this.processNewData(data, nodeOptions ?? {}, requestVersion, isLazyLoad);
      },
      () => {
        this.dataLoadCompleted.emit();
        this._cdr.markForCheck();
      }
    );
  }

  protected onFiltering($event: { value: string }, side: TreeSide): void {
    if (side === 'right') return;
    this.filterTreeView($event.value, side);
  }

  private filterDebounceTime = 750;
  private filterDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
  private filterTreeView(filterTerm: string, side: TreeSide): void {
    if (this.filterDebounceTimeout) clearTimeout(this.filterDebounceTimeout);
    this.filterDebounceTimeout = setTimeout(() => {
      if (
        filterTerm.length < DualTreeViewComponent.MIN_SEARCH_CHARACTERS_REQUIRED
      ) {
        this._cdr.markForCheck();
      }
      this.filterTreeImpl(filterTerm, side);
    }, this.filterDebounceTime);
  }

  private updateLeftTreeData(data: TreeNode[]): void {
    this.leftDataSourceFiltered = data;
    this.invalidateLeftFlattenCache();
    this.updateChildCounts(this.leftDataSourceFiltered);
    this._hasNodesLeft = this.leftDisplayList.length > 0;
    this._hasCheckedNodesLeft = this.leftDisplayList.some((n) => n.isChecked);
    this._cdr.markForCheck();
  }

  private updateRightTreeData(data: TreeNode[]): void {
    this.rightDataSource = data;
    this.invalidateRightFlattenCache();
    this.updateChildCounts(this.rightDataSource);
    this._hasNodesRight = data.length > 0;
    this._hasCheckedNodesRight = data.some((n) => n.isChecked);
    this.rightTreeDataChanged.emit(this.rightDataSource);
    this._cdr.markForCheck();
  }

  private moveLocalNodes(fromSide: TreeSide, toSide: TreeSide): void {
    const checkedNodeIds = this.getCheckedIds(fromSide);
    if (!checkedNodeIds.length) return;
    this.setCursorToWait();
    const expandedIds = this.captureExpandedIds();
    const fromTreeData =
      fromSide === 'left' ? this.leftDisplayList : this.rightDataSource;
    const parentLookup =
      fromSide === 'left' ? this.leftDataSourceFiltered : this.rightDataSource;

    const nodesToMoveMap = new Map<string, TreeNode>();
    const getAllDescendants = (nodeId: string) => {
      const node = fromTreeData.find((n) => n.id === nodeId);
      if (node && !nodesToMoveMap.has(node.id)) {
        nodesToMoveMap.set(node.id, node);
        fromTreeData
          .filter((n) => n.parentId === nodeId)
          .forEach((c) => getAllDescendants(c.id));
      }
    };
    checkedNodeIds.forEach((id) => getAllDescendants(id));
    const allNodesToMove = Array.from(nodesToMoveMap.values());
    const nodesToRemoveIds = new Set(allNodesToMove.map((n) => n.id));

    if (fromSide === 'left' && toSide === 'right') {
      const existingRightNodeIds = new Set(
        this.rightDataSource.map((n) => n.id)
      );
      const parentNodesToAdd = new Map<string, TreeNode>();
      const getParents = (node: TreeNode) => {
        if (
          !node.parentId ||
          nodesToMoveMap.has(node.parentId) ||
          existingRightNodeIds.has(node.parentId)
        )
          return;
        const parentNode = parentLookup.find((n) => n.id === node.parentId);
        if (parentNode && !parentNodesToAdd.has(parentNode.id)) {
          parentNodesToAdd.set(parentNode.id, { ...parentNode });
          getParents(parentNode);
        }
      };
      allNodesToMove.forEach(getParents);
      const nodesForRightTree = [
        ...allNodesToMove,
        ...Array.from(parentNodesToAdd.values()),
      ];
      const newRightDataSource = [...this.rightDataSource];
      nodesForRightTree.forEach((node) => {
        if (!existingRightNodeIds.has(node.id)) {
          newRightDataSource.push(node);
          existingRightNodeIds.add(node.id);
        }
      });
      this.updateRightTreeData(newRightDataSource);
      this.leftDataSourceFiltered = this.leftDataSourceFiltered.filter(
        (n) => !nodesToRemoveIds.has(n.id)
      );
      this.updateLeftTreeData(this.leftDataSourceFiltered);
    } else if (fromSide === 'right' && toSide === 'left') {
      const currentLeftIds = new Set(
        this.leftDataSourceFiltered.map((n) => n.id)
      );
      const parentNodesToAdd = new Map<string, TreeNode>();
      const getParents = (node: TreeNode) => {
        if (
          !node.parentId ||
          nodesToMoveMap.has(node.parentId) ||
          currentLeftIds.has(node.parentId)
        )
          return;
        const parentNode = parentLookup.find((n) => n.id === node.parentId);
        if (parentNode && !parentNodesToAdd.has(parentNode.id)) {
          parentNodesToAdd.set(parentNode.id, { ...parentNode });
          getParents(parentNode);
        }
      };
      allNodesToMove.forEach(getParents);
      const nodesForLeftTree = [
        ...allNodesToMove,
        ...Array.from(parentNodesToAdd.values()),
      ];
      const nodesToMoveIds = new Set(nodesForLeftTree.map((n) => n.id));
      this.rightDataSource = this.rightDataSource.filter(
        (node) => !nodesToRemoveIds.has(node.id)
      );
      
      const emptyParentIds = new Set<string>();
      const checkForEmptyParents = () => {
        let foundEmpty = false;
        this.rightDataSource.forEach((node) => {
          if (node.hasChildren && !emptyParentIds.has(node.id)) {
            const hasRemainingChildren = this.rightDataSource.some(
              (n) => n.parentId === node.id && !emptyParentIds.has(n.id)
            );
            if (!hasRemainingChildren) {
              emptyParentIds.add(node.id);
              foundEmpty = true;
            }
          }
        });
        if (foundEmpty) checkForEmptyParents();
      };
      checkForEmptyParents();
      
      if (emptyParentIds.size > 0) {
        this.rightDataSource = this.rightDataSource.filter(
          (node) => !emptyParentIds.has(node.id)
        );
      }
      
      this.updateRightTreeData(this.rightDataSource);
      const nodesToAdd = nodesForLeftTree.filter((n) => !currentLeftIds.has(n.id));
      this.leftDataSourceFiltered.push(...nodesToAdd);
      this.leftDataSourceFiltered.sort((a, b) => {
        const levelA = a.level ?? 0;
        const levelB = b.level ?? 0;
        if (levelA !== levelB) return levelA - levelB;
        return a.name.localeCompare(b.name);
      });
      this.updateLeftTreeData(this.leftDataSourceFiltered);
    }

    fromTreeData.forEach((n) => (n.isChecked = false));
    if (fromSide === 'left') this._hasCheckedNodesLeft = this.leftDisplayList.some((n) => n.isChecked);
    else this._hasCheckedNodesRight = false;
    this.restoreExpandedIds(expandedIds);
    this.removeCursorWait();
  }

  private filterTreeImpl(filterTerm: string, side: TreeSide): void {
    if (side !== 'left') return;
    const trimmed = filterTerm?.trim() ?? '';
    this.leftFilterTerm = trimmed;
    this.leftFilterTokens = this.getFilterTokens(trimmed);
    const isFullQuery =
      trimmed.length <
      DualTreeViewComponent.MIN_SEARCH_CHARACTERS_REQUIRED;
    const query = isFullQuery
      ? new Query()
      : new Query().where('name', 'contains', trimmed, true);
    this.refreshLeftData(query);
  }

  private processNewData(
    data: any,
    nodeOptions: Record<string, unknown>,
    requestVersion: number,
    isLazyLoad: boolean
  ): void {
    if (!isLazyLoad && requestVersion !== this.leftRefreshRequestVersion) {
      this.dataLoadCompleted.emit();
      this._cdr.markForCheck();
      return;
    }
    const results = (this.dataMapper(data) as TreeNode[]).map((node) =>
      this.normalizeNodeLoadState({
        ...node,
        ...nodeOptions,
      })
    );
    const rightTreeNodeIds = new Set(
      this.rightDataSource.map((node) => node.id)
    );
    const filteredResults = results.filter(
      (node) => !rightTreeNodeIds.has(node.id)
    );
    this.applyLeftNodeMatchState(filteredResults);
    this.updateLeftTreeData(filteredResults);
    this.dataLoadCompleted.emit();
    this._cdr.markForCheck();
  }

  private applyLeftNodeMatchState(nodes: TreeNode[]): void {
    if (!this.leftFilterTokens.length) {
      nodes.forEach((n) => {
        n.isMatch = false;
        n.matchStart = undefined;
        n.matchEnd = undefined;
      });
      return;
    }

    nodes.forEach((n) => {
      const nodeName = n?.name ?? '';
      const matchedToken = this.getMatchingToken(nodeName, this.leftFilterTokens);
      n.isMatch = !!matchedToken;

      if (!matchedToken) {
        n.matchStart = undefined;
        n.matchEnd = undefined;
        return;
      }

      const start = nodeName.toLocaleLowerCase().indexOf(matchedToken.toLocaleLowerCase());
      if (start >= 0) {
        n.matchStart = start;
        n.matchEnd = start + matchedToken.length;
      } else {
        n.matchStart = undefined;
        n.matchEnd = undefined;
      }
    });
  }

  private getFilterTokens(filterTerm: string): string[] {
    if (!filterTerm) return [];

    const tokens: string[] = [];
    let currentToken = '';
    let quoteChar: '"' | "'" | null = null;

    const pushToken = () => {
      const token = currentToken.trim();
      if (token.length > 0) {
        tokens.push(token);
      }
      currentToken = '';
    };

    for (const ch of filterTerm) {
      if (quoteChar) {
        if (ch === quoteChar) {
          pushToken();
          quoteChar = null;
        } else {
          currentToken += ch;
        }
        continue;
      }

      if (ch === '"' || ch === "'") {
        pushToken();
        quoteChar = ch;
        continue;
      }

      if (/\s|,|;/.test(ch)) {
        pushToken();
        continue;
      }

      currentToken += ch;
    }

    pushToken();

    return Array.from(new Set(tokens.map((token) => token.toLocaleLowerCase())));
  }

  private getMatchingToken(nodeName: string, tokens: string[]): string | null {
    if (!nodeName || !tokens.length) return null;

    const normalizedNodeName = nodeName.toLocaleLowerCase();
    for (const token of tokens) {
      const matched =
        this.isShortToken(token)
          ? normalizedNodeName.startsWith(token)
          : normalizedNodeName.includes(token);

      if (matched) {
        return token;
      }
    }

    return null;
  }

  private isShortToken(token: string): boolean {
    return token.length <= SHORT_TOKEN_LENGTH;
  }

  private normalizeNodeLoadState(node: TreeNode): TreeNode {
    if (node.hasChildren && node.areChildrenLoaded == null) {
      node.areChildrenLoaded = false;
    }
    return node;
  }
}
